<template>
	<div class="row">
		<div class="col-12">
			<h2>{{ $t('invoice.supplier_control_form') }}</h2>
			<WarnAlert v-if="from_xml" messageI18n="invoice.supplier_control_from_xml" />
		</div>

		<div class="col-7">
            <iframe v-if="invoice_supplier.suppliersocr_pdf_url.includes('.pdf')" :src="invoice_supplier.suppliersocr_pdf_url" height="1000px" width="100%" style="position:relative"></iframe>
            <img v-else :src="invoice_supplier.suppliersocr_pdf_url" class="w-100"/>
		</div>
		<!-- Fournisseur non reconnu : plutôt que de laisser l'opérateur chercher dans une liste, on lui
		     propose ce que le document déclare, complété par l'annuaire des entreprises. -->
		<div v-if="invoice_supplier_local && !invoice_supplier_local.tier" class="col-5">
			<SupplierSuggestion :invoice="invoice_supplier_local" @linked="onTiersLinked" />
		</div>

		<!-- Fournisseur reconnu, mais compte de règlement inconnu ou non confirmé : la décision
		     précède le contrôle. Laisser le formulaire accessible reviendrait à faire approuver une
		     facture dont les coordonnées bancaires n'ont été vérifiées par personne. -->
		<div v-else-if="invoice_supplier_local && iban_to_arbitrate" class="col-5">
			<IbanControl
				:invoicesupplier_id="invoice_supplier.suppliersocr_id"
				:check="iban_check"
				@decided="onIbanDecided"
			/>
		</div>

		<div v-else-if="invoice_supplier_local" class="col-5">
			<div class="form-group">
				<label>{{ $t('invoice.entity_invoiced') }} *</label>
				<e-select
					v-model="invoice_supplier_local.entity"
					id="entity_id"
					track-by="tiers_id"
					label="tiers_rs"
					:placeholder="$t('tiers.rechercher_billing_entity')"
					:selectedLabel="$t('global.selected_label')"
					:options="entity"
					:searchable="true"
					:allow-empty="false"
					:loading="loadingEntity"
					:show-labels="false"
				>
					<template slot="singleLabel" slot-scope="{ option }">{{ option.tiers_rs }}</template>
					<template slot="noOptions">{{ $t('global.list_empty') }}</template>
				</e-select>
			</div>
			<div class="form-group">
				<label>{{ $t('tiers.tiers') }} *</label>

				<div class="row">
					<div class="col">
						<e-select
							v-model="invoice_supplier_local.tier"
							track-by="tiers_id"
							label="tiers_rs"
							:placeholder="$t('tiers.rechercher')"
							:selectedLabel="$t('global.selected_label')"
							:options="tiers"
							:searchable="true"
							:allow-empty="false"
							:loading="loadingTiers"
							:show-labels="false"
						>
							<template slot="singleLabel" slot-scope="{ option }">{{ option.tiers_rs }} {{ option.tiers_postalcode }}</template>
							<template slot="option" slot-scope="{ option }">{{ option.tiers_rs }} {{ option.tiers_postalcode }}</template>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>
					<div class="col-auto">
		                <button class="btn btn-secondary" @click="addTiers"><font-awesome-icon :icon="['far', 'plus']" /></button>
		            </div>
		            <div class="col-12" v-if="pappers_key && invoice_supplier.tier && (invoice_supplier.tier.tiers_siren || invoice_supplier.tier.tiers_siret)">
						<span class="btn btn-link" @click="askPappers">{{ $t('invoice.update_pappers') }} <font-awesome-icon :icon="['fal', 'long-arrow-right']" /></span>
					</div>
				</div>
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_title') }}</label>
				<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_title">
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_num') }}</label>
				<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_num" :disabled="from_xml">
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_date') }}</label>
				<!-- Le sélecteur de date n'a pas d'état inactif : un champ figé le remplace sur une
				     facture électronique, dont la date vient du document. -->
				<input v-if="from_xml" type="text" class="form-control" :value="formatDate(invoice_supplier_local.suppliersocr_date)" disabled>
				<e-datepicker v-else v-model="invoice_supplier_local.suppliersocr_date" mode="date"/>
			</div>
			<!-- Les montants sont ceux du document : le symbole de sa devise les accompagne, sans quoi une
				 facture en dollars se lirait en euros à côté de sa contre-valeur, qui l'est vraiment. -->
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_ht') }} *</label>
				<div class="input-group">
					<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_ht" :disabled="!can_edit || from_xml">
					<div class="input-group-append">
						<span class="input-group-text">{{ currency_symbol }}</span>
					</div>
				</div>
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_ttc') }} *</label>
				<div class="input-group">
					<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_ttc" :disabled="!can_edit || from_xml">
					<div class="input-group-append">
						<span class="input-group-text">{{ currency_symbol }}</span>
					</div>
				</div>
			</div>
			<div v-if="!can_edit">
				<WarnAlert messageI18n="invoice.supplier_edit_ttc" />
			</div>
			<!-- <div class="form-group">
				<label>{{ $t('invoice.suppliersocr_balance') }}</label>
				<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_balance">
			</div> -->
			<!-- Un avoir n'annule pas le fournisseur en général, il annule une facture précise : c'est ce
				 rattachement qui leur fait partager une lettre de lettrage, et donc solder le compte
				 fournisseur au lieu d'y laisser deux lignes ouvertes. Il reste saisissable sur une facture
				 électronique : c'est une décision interne, pas une donnée du document. -->
			<div v-if="is_credit_note" class="form-group border-top pt-2">
				<label>{{ $t('invoice.supplier_credit_of') }}</label>
				<e-select
					v-model="credit_invoices"
					track-by="suppliersocr_id"
					label="suppliersocr_num"
					:placeholder="$t('invoice.supplier_search_credited')"
					:selectedLabel="$t('global.selected_label')"
					:options="creditable_invoices"
					:multiple="true"
					:searchable="true"
					:internal-search="false"
					:loading="loading_creditable"
					:show-labels="false"
					@search-change="debouncedSearchCreditable"
				>
					<template slot="option" slot-scope="{ option }">{{ option.suppliersocr_num }} — {{ formatDate(option.suppliersocr_date) }} — {{ option.ttc_format }} — {{ $t('invoice.supplier_credit_balance', { amount: option.balance_format }) }}</template>
					<template slot="noOptions">{{ $t('global.list_empty') }}</template>
				</e-select>
				<div v-if="uncovered_credit_invoices.length > 0" class="alert alert-warning mt-2">{{ $t('invoice.supplier_credit_exhausted', { nums: uncovered_credit_invoices.join(', ') }) }}</div>
			</div>
			<div class="d-flex">
				<label>{{ $t('invoice.supplier_duedate') }}</label>
				<!-- L'échéancier d'une facture électronique est celui que le document porte (BT-9) : on
					 ne le fractionne pas depuis cet écran. -->
				<a v-if="!from_xml" href="#" @click.prevent="add_duedate"><font-awesome-icon :icon="['fal', 'plus']" class="ml-1"/></a>
			</div>
			<div v-for="(duedate, index) in duedates" :key="index" class="row border-top pt-1">
				<div class="col-6 form-group">
					<label>{{ $t('invoice.suppliersocr_duedate') }}</label>
					<input v-if="from_xml" type="text" class="form-control" :value="formatDate(duedate.isduedate_date)" disabled>
					<e-datepicker v-else v-model="duedate.isduedate_date" mode="date"/>
				</div>
				<div class="col-6 form-group">
					<label>{{ $t('invoice.suppliersocr_duedate_amount') }}</label>
					<div class="input-group">
						<input type="text" class="form-control" v-model="duedate.isduedate_balance" :disabled="from_xml">
						<div class="input-group-append">
							<span class="input-group-text">{{ currency_symbol }}</span>
						</div>
					</div>
				</div>
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_currency') }}</label>
				<input v-if="from_xml" type="text" class="form-control" :value="invoice_supplier_local.suppliersocr_currency" disabled>
				<CurrencyInput v-else v-model="invoice_supplier_local.suppliersocr_currency" />
			</div>
			<!-- Ce qui sera réellement comptabilisé, montré avant l'enregistrement et non après : les
				 montants ci-dessus sont ceux du document, la comptabilité retient leur contre-valeur. -->
			<div v-if="is_foreign_currency" class="form-group border-top pt-2">
				<template v-if="converted_display">
					<label>{{ $t('invoice.supplier_accounted_amounts', { currency: ledger_currency }) }}</label>
					<div class="d-flex justify-content-between">
						<span>{{ $t('invoice.suppliersocr_ht') }}</span>
						<span>{{ converted_display.ht }}</span>
					</div>
					<div class="d-flex justify-content-between">
						<span>{{ $t('invoice.suppliersocr_ttc') }}</span>
						<span>{{ converted_display.ttc }}</span>
					</div>
					<small class="text-muted">
						{{ $t('invoice.supplier_exchange_rate', {
							rate: exchange_rate.rate,
							currency: invoice_supplier_local.suppliersocr_currency,
							date: formatDate(exchange_rate.date)
						}) }}
					</small>
				</template>
				<!-- Tant que la date manque, il n'y a pas de cours à chercher : annoncer qu'aucun n'est
					 publié désignerait la devise alors que c'est la facture qui est incomplète. -->
				<WarnAlert v-else-if="invoice_supplier_local.suppliersocr_date" messageI18n="invoice.supplier_no_exchange_rate" />
			</div>
			<div class="form-group">
				<label>{{ $t('invoice.suppliersocr_memo') }}</label>
				<input type="text" class="form-control" v-model="invoice_supplier_local.suppliersocr_memo">
			</div>
			<div class="form-group mt-3 text-center">
				<button @click="save()" class="btn btn-secondary mb-2">
					{{ $t('global.enregistrer') }} <font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin /><font-awesome-icon v-else :icon="['far', 'save']" />
				</button>

				<!-- La vérification vaut prise en charge auprès du fournisseur : elle n'est proposée
					 qu'une fois, et disparaît une fois acquise. -->
				<button v-if="!is_checked && !is_closed" @click="save('checked')" class="btn btn-primary mb-2 ml-2" :disabled="processing">
					{{ $t('invoice.supplier_mark_checked') }}
				</button>

				<button v-if="!is_approved && !is_closed" @click="save('approved')" class="btn btn-primary mb-2 ml-2" :disabled="processing">
					{{ $t('invoice.supplier_mark_approved') }}
				</button>

				<div v-if="is_closed" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_lifecycle_closed') }}</div>
				<div v-else-if="is_approved" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_already_approved') }}</div>
			</div>
		</div>

		<!-- La suite se choisit par l'un des boutons : refermer d'un clic à côté laisserait le
		     formulaire ouvert sur une facture déjà traitée. -->
		<b-modal ref="modalNext" hide-footer hide-header-close no-close-on-backdrop no-close-on-esc>
			<template v-slot:modal-title>{{ $t('invoice.supplier_next_title') }}</template>

			<p>{{ $t(done_intent === 'approved' ? 'invoice.supplier_next_approved' : 'invoice.supplier_next_checked') }}</p>

			<div class="text-center">
				<button v-if="done_intent === 'approved'" class="btn btn-primary mb-2" @click="goToPayment">
					{{ $t('invoice.supplier_next_payment') }} <font-awesome-icon :icon="['fal', 'money-check-edit-alt']" />
				</button>

				<button v-if="next_invoice" class="btn btn-primary mb-2 ml-2" @click="goToNext">
					{{ $t('invoice.supplier_next_invoice', { num: next_invoice.suppliersocr_num }) }} <font-awesome-icon :icon="['fal', 'arrow-right']" />
				</button>

				<!-- Plus rien à traiter dans cette vue : le dire vaut mieux qu'un bouton absent. -->
				<div v-else class="invoice-supplier-details__hint mb-2">{{ $t('invoice.supplier_next_none') }}</div>

				<div>
					<button class="btn btn-secondary mt-2" @click="closeNext">{{ $t('global.terminer') }}</button>
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script type="text/javascript">
    import Common from '@/assets/js/common'
    import _debounce from 'lodash/debounce'
    import Tiers from "@/mixins/Tiers.js"
	import Invoice from "@/mixins/Invoice.js"
	import Shutter from "@/mixins/Shutter.js"
	import ShutterInvoice from "@/mixins/shutters-manager/Invoice.js"

	export default {
		name: 'ControlForm',

		mixins: [Tiers, Invoice, Shutter, ShutterInvoice],

		props: ['invoice_supplier'],

		data () {
			return {
				// Geste qui vient d'être posé, et facture proposée ensuite.
				done_intent: '',
				next_invoice: null,
				invoice_supplier_local: null,
				exchange_rate: null,
				converted_display: null,
				loadingEntity: false,
				loadingTiers: false,
				entity: [],
				tiers: [],
				processing: false,
				pappers_key: null,
				can_edit: true,
				duedates: [],
				// Factures que cet avoir annule, et celles qu'il pourrait annuler.
				credit_invoices: [],
				creditable_invoices: [],
				loading_creditable: false,
				debouncedSearchCreditable: _debounce(this.searchCreditable, 300),
				iban_check: null,
				iban_arbitrated: false
			}
		},

		mounted() {
			this.init_component()
		},

		methods: {
			async init_component() {
				const invoice = this.invoice_supplier

				// L'écran montre le PDF et se saisit en regard : ses montants sont ceux du document,
				// quand les clés ordinaires portent la contrepartie comptable que lisent la ventilation,
				// le FEC et le rapprochement. Hors devise étrangère, les deux se confondent.
				if(invoice.suppliersocr_ht_src !== null) {
					invoice.suppliersocr_ht = invoice.suppliersocr_ht_src
					invoice.suppliersocr_ttc = invoice.suppliersocr_ttc_src
					invoice.suppliersocr_balance = invoice.suppliersocr_balance_src
					invoice.duedates.forEach(duedate => { duedate.isduedate_balance = duedate.isduedate_balance_src })
				}

				invoice.suppliersocr_date = invoice.suppliersocr_date ? new Date(invoice.suppliersocr_date) : null
				invoice.suppliersocr_duedate = invoice.suppliersocr_duedate ? new Date(invoice.suppliersocr_duedate) : null
				invoice.suppliersocr_checked = invoice.suppliersocr_ocr_status == 'checked'
				this.invoice_supplier_local = invoice

				// Ce qui fige les montants est l'existence d'une imputation, pas le statut de paiement :
				// c'est ce que dit l'alerte affichée juste en dessous. Le statut s'en approchait mal — un
				// avoir dont le reste dû avait divergé du TTC sortait « partiellement payé » sans porter
				// le moindre règlement, et se retrouvait verrouillé sans que rien ne puisse le corriger.
				this.can_edit = !this.invoice_supplier.suppliersocr_has_payments

				const config = this.getConfig('api_config')
                const pappers = config.find(conf => conf.apiconfig_type == "PAP")
                if(pappers) {
                    this.pappers_key = pappers.apiconfig_key
                }

				this.loadingEntity = true
				const accounting = await this.loadAccountingPlans()
				const accounting_supplier = accounting.filter(acc => acc.accountingplan_supplier)
				this.entity = accounting_supplier.map(acc => acc.tiers)
				this.loadingEntity = false

				this.loadingTiers = true
				this.tiers = await this.loadTiers()
				this.loadingTiers = false

				if(this.invoice_supplier.suppliersocr_tiers) {
					this.invoice_supplier.tier = this.tiers.find(tier => tier.tiers_id == this.invoice_supplier.suppliersocr_tiers)
				}

				await this.loadIbanCheck()
				await this.loadExchangeRate()

				this.duedates = this.invoice_supplier.duedates.map(duedate => ({...duedate, isduedate_date: new Date(duedate.isduedate_date), isduedate_balance: duedate.isduedate_balance/100}))
				if(this.duedates.length == 0 && this.invoice_supplier.suppliersocr_duedate) {
					this.duedates.push({
						isduedate_id: 0,
						isduedate_date: this.invoice_supplier.suppliersocr_duedate,
						isduedate_balance: this.invoice_supplier.suppliersocr_balance
					})
				}

				// Les factures déjà rattachées n'ont pas besoin du libellé de leur montant : le sélecteur
				// n'affiche que leur numéro une fois sélectionnées. Leur liste de choix, elle, se charge
				// par l'observateur de is_credit_note, que l'arrivée de la facture de travail déclenche.
				this.credit_invoices = this.invoice_supplier.credit_invoices
			},

			/**
			 * Recherche les factures que cet avoir peut annuler.
			 *
			 * Le fournisseur et la comptabilité sont ceux du formulaire, pas ceux de la facture enregistrée :
			 * ce sont eux que l'opérateur corrige au contrôle, et chercher sur la pièce enregistrée
			 * proposerait les factures du fournisseur que la lecture optique avait retenu à tort.
			 *
			 * Les libellés des montants sont préparés ici : ils arrivent en centimes de la devise de tenue,
			 * et leur formatage est asynchrone, donc impossible dans le gabarit.
			 *
			 * @param {String} search Numéro de facture, tel qu'il est imprimé sur le document.
			 */
			async searchCreditable(search) {
				this.loading_creditable = true

				const invoices = await this.getCreditableInvoiceSupplier(this.invoice_supplier.suppliersocr_id, search, this.invoice_supplier_local.suppliersocr_tiers, this.invoice_supplier_local.suppliersocr_entity)

				await Common.asyncForEach(invoices, async (invoice) => {
					invoice.ttc_format = await this.priceFormat(invoice.suppliersocr_ttc / 100, invoice.suppliersocr_ledger_currency, true)
					invoice.balance_format = await this.priceFormat(invoice.suppliersocr_balance / 100, invoice.suppliersocr_ledger_currency, true)
				})

				this.creditable_invoices = invoices
				this.loading_creditable = false
			},

			/**
			 * Le fournisseur ou la comptabilité de l'avoir a été corrigé : les factures proposées et
			 * désignées étaient celles de l'ancien.
			 *
			 * @param {Number} value Nouvel identifiant.
			 * @param {Number} previous Ancien identifiant, absent à l'initialisation du formulaire.
			 */
			onCreditScopeChanged(value, previous) {
				if(!previous || value == previous) {
					return
				}

				this.credit_invoices = []

				if(this.is_credit_note) {
					this.searchCreditable('')
				}
			},

			/**
			 * Enregistre la facture, et selon le bouton actionné, la déclare vérifiée ou approuvée.
			 *
			 * Les trois gestes partagent le même enregistrement : ce qui les distingue est ce qu'ils
			 * engagent ensuite auprès du fournisseur — rien, une prise en charge (204), ou une prise en
			 * charge suivie d'une approbation (205). Le serveur ne pose que ce qui manque.
			 *
			 * @param {String} intent Vide, 'checked' ou 'approved'.
			 */
			async save(intent = '') {
				if(!this.invoice_supplier_local.tier || !this.invoice_supplier_local.entity) {
					this.failureToast('invoice.supplier_error_control')
					return false
				}

				// Le serveur refuse de même : autant le dire avant l'envoi, avec le numéro en cause, que
				// laisser l'enregistrement échouer sur une erreur générique.
				if(this.is_credit_note && this.uncovered_credit_invoices.length > 0) {
					this.failureToast('invoice.supplier_credit_exhausted', false, { nums: this.uncovered_credit_invoices.join(', ') })
					return false
				}

				this.processing = true

				// Une facture approuvée est vérifiée par construction : le serveur le rétablit, mais
				// autant ne pas lui envoyer un état contradictoire.
				this.invoice_supplier_local.suppliersocr_checked = intent !== '' || this.is_checked

				if(this.invoice_supplier_local.tier.tiers_id < 0) {
					await this.$sync.force()
					this.invoice_supplier_local.tier.tiers_id = this.$sync.replaceWithReplicated('tiers', this.invoice_supplier_local.tier.tiers_id)
				}

				this.invoice_supplier_local.suppliersocr_ttc = this.invoice_supplier_local.suppliersocr_ttc.toString().replace(',', '.')
				this.invoice_supplier_local.suppliersocr_ht = this.invoice_supplier_local.suppliersocr_ht.toString().replace(',', '.')

				if(this.can_edit) {
					this.invoice_supplier_local.suppliersocr_balance = this.invoice_supplier_local.suppliersocr_ttc.toString().replace(',', '.')
				}

				const duedate = this.duedates.length === 1 ? this.duedates[0] : null

				// Une facture dont l'échéancier tient en une seule échéance encore intacte doit tout à
				// cette échéance : elle suit donc le TTC. Sans cela, corriger le signe d'un avoir laissait
				// son échéance positive, et le serveur — qui recompose le reste dû depuis l'échéancier —
				// réinstallait un solde positif face à un TTC négatif. La pièce ressortait « partiellement
				// soldée » sans porter le moindre règlement. Un échéancier fractionné, lui, appartient à
				// l'opérateur : on n'y touche pas.
				if(this.can_edit && duedate !== null && (!duedate.isduedate_id || Math.round(duedate.isduedate_balance * 100) === duedate.isduedate_amount)) {
					duedate.isduedate_balance = parseFloat(this.invoice_supplier_local.suppliersocr_ttc)
				}

				this.invoice_supplier_local.duedates = this.duedates
				this.invoice_supplier_local.credit_invoice_ids = this.is_credit_note ? this.credit_invoices.map(invoice => invoice.suppliersocr_id) : []

				const invoice = await this.updateInvoiceSupplier(this.invoice_supplier_local.suppliersocr_id, this.invoice_supplier_local)

				// L'approbation est un second geste : elle pose le bon à payer et, sur une facture
				// électronique, la déclare au fournisseur.
				if(invoice && intent === 'approved') {
					await this.approveInvoiceSupplierPayment(this.invoice_supplier_local.suppliersocr_id)
				}

				this.processing = false

				// Le rapprochement du fournisseur a pu être reporté sur d'autres factures reçues de la
				// même adresse : elles ont changé sans que l'utilisateur les ait sous les yeux.
				if(invoice && invoice.propagated_tiers_invoices > 0) {
					this.infoToast('invoice.supplier_tiers_propagated', { nb: invoice.propagated_tiers_invoices })
				}

				// Le rattachement a soldé l'avoir contre sa facture : c'est un mouvement comptable, il se
				// dit plutôt que de se découvrir sur l'onglet des règlements.
				if(invoice && invoice.compensated) {
					this.infoToast('invoice.supplier_compensate_done')
				}

				this.successToast('toast.info_modif_succes')

				// Un contrôle mène rarement seul : la facture traitée, on propose la suite du travail —
				// la suivante de la pile, ou le règlement de celle-ci quand elle vient d'être approuvée.
				if(invoice && intent !== '') {
					await this.proposeNext(intent)
					return
				}

				this.ok()
				this.shutterPanel().close('invoice-supplier-control-form')
			},

			/**
			 * Ouvre le volet de suite. La facture suivante est cherchée dans la vue que l'utilisateur
			 * était en train de vider : la boîte de réception après une vérification, les factures à
			 * approuver après une approbation.
			 */
			async proposeNext(intent) {
				this.done_intent = intent

				const bucket = intent === 'approved' ? 'to-process' : 'inbox'
				this.next_invoice = await this.getNextInvoiceSupplier(bucket, this.invoice_supplier_local.suppliersocr_id, this.getConfig('invoice_suppliers_entities') || [])

				this.$refs.modalNext.show()
			},

			/**
			 * Enchaîne sur la facture suivante, ouverte sur son onglet de contrôle.
			 *
			 * La fiche n'est délibérément pas prévenue de l'enregistrement : elle rechargerait la
			 * facture que l'on quitte, et sa réponse, arrivant après la navigation, écraserait celle
			 * vers laquelle on va. Le changement d'identifiant suffit à la faire se refaire.
			 */
			goToNext() {
				const invoicesupplier_id = this.next_invoice.suppliersocr_id

				this.dismissNext()
				this.$router.push({ name: 'invoiceSupplierFicheControl', params: { invoicesupplier_id } })
			},

			/**
			 * Passe au règlement de la facture qui vient d'être approuvée, saisie ouverte. La fiche est
			 * prévenue : elle reste sur la même facture, dont le bandeau doit porter le nouveau statut.
			 */
			goToPayment() {
				const invoicesupplier_id = this.invoice_supplier_local.suppliersocr_id

				this.ok()
				this.dismissNext()
				this.$router.push({ name: 'invoiceSupplierFichePayment', params: { invoicesupplier_id }, query: { add: 1 } })
			},

			closeNext() {
				this.ok()
				this.dismissNext()
			},

			dismissNext() {
				this.$refs.modalNext.hide()
				this.shutterPanel().close('invoice-supplier-control-form')
			},

			/**
			 * Ouvre la création d'un tiers pré-rempli de ce que le document déclare. Rien n'est tenu
			 * pour acquis : une facture peut n'avoir livré aucune identité exploitable, et l'écran
			 * doit alors ouvrir un formulaire vide plutôt que de rompre.
			 */
			addTiers() {
				const data = this.invoice_supplier_local.suppliersocr_ocr_data
				const prediction = (data ? JSON.parse(data).prediction : null) || {}
				const registrations = prediction.supplier_company_registrations || []

				let siret = registrations.find(r => r.type == 'SIRET')
				let siren = registrations.find(r => r.type == 'SIREN')
				let vat = registrations.find(r => r.type == 'VAT NUMBER')
				let iban = (prediction.supplier_payment_details || [])[0]

				let tiers = {
					tiers_rs: prediction.supplier_name ? prediction.supplier_name.value : '',
					tiers_address1: prediction.supplier_address ? prediction.supplier_address.value : '',
					tiers_siret: siret ? siret.value : '',
					tiers_siren: siren ? siren.value : '',
					tiers_vat_number: vat ? vat.value : '',
					tiers_rib: iban ? iban.iban : ''
				}
				
				this.setupAddTier(tiers, this.reloadTiers)
			},

			/**
			 * Le fournisseur vient d'être rattaché depuis le panneau de suggestion : le formulaire
			 * prend la suite, avec sa liste rafraîchie et le tiers sélectionné.
			 */
			async onTiersLinked(tiers_id) {
				this.invoice_supplier_local.suppliersocr_tiers = tiers_id

				await this.reloadTiers(tiers_id)
			},

			/**
			 * Confronte l'IBAN de la facture aux coordonnées connues du fournisseur retenu.
			 *
			 * Le verdict est redemandé au serveur à chaque changement de fournisseur : le même
			 * document, rangé derrière un autre tiers, n'appelle pas la même conclusion.
			 */
			/**
			 * Va chercher le cours applicable à la devise et à la date saisies.
			 *
			 * Une facture déjà enregistrée en porte un, figé : c'est celui-là qui vaut, et le
			 * redemander afficherait le cours du jour à la place de celui qui l'a comptabilisée.
			 */
			async loadExchangeRate() {
				if(!this.is_foreign_currency || !this.invoice_supplier_local.suppliersocr_date) {
					this.exchange_rate = null
					return
				}

				if(this.invoice_supplier.suppliersocr_rate) {
					this.exchange_rate = {
						rate: this.invoice_supplier.suppliersocr_rate,
						date: this.invoice_supplier.suppliersocr_rate_date,
						source: this.invoice_supplier.suppliersocr_rate_source
					}
					return
				}

				const date = this.invoice_supplier_local.suppliersocr_date.toISOString().slice(0, 10)
				this.exchange_rate = await this.getExchangeRate(this.invoice_supplier_local.suppliersocr_currency, date)
			},

			async loadIbanCheck() {
				// Le fournisseur a changé : ce qui avait été tranché ne portait pas sur ce tiers-là.
				this.iban_arbitrated = false

				if(!this.invoice_supplier_local.suppliersocr_tiers) {
					this.iban_check = null
					return
				}

				this.iban_check = await this.checkInvoiceSupplierIban(this.invoice_supplier_local.suppliersocr_id)
			},

			/**
			 * L'utilisateur a tranché sur l'IBAN : le formulaire de contrôle prend la suite.
			 *
			 * La mise en quarantaine laisse passer elle aussi. Barrer la route à qui vient de signaler
			 * un compte douteux enfermerait l'utilisateur sur cet écran, et la seule issue serait
			 * d'approuver le compte pour avancer — exactement ce que l'avertissement cherche à éviter.
			 * La quarantaine est enregistrée, et reste signalée sur la fiche de la facture.
			 *
			 * @param {Object} check Nouveau verdict renvoyé par le serveur.
			 */
			onIbanDecided(check) {
				this.iban_check = check
				this.iban_arbitrated = true
			},

			async reloadTiers(tiers_id) {
				this.shutterPanel().goTo('invoice-supplier-control-form')

				this.loadingTiers = true
				this.tiers = await this.loadTiers()
				this.loadingTiers = false

				this.invoice_supplier_local.tier = this.tiers.find(tier => tier.tiers_id == tiers_id)
			},

			askPappers() {
				this.setupSuggestionPappers(this.invoice_supplier_local.tier, this.reloadTiers)
			},

			add_duedate() {
				this.duedates.push({
					isduedate_id: 0,
					isduedate_date: new Date(),
					isduedate_balance: 0
				})
			}
		},

		watch: {
			'invoice_supplier_local.entity': {
                handler(val) {
                	if(val) {
	                	this.invoice_supplier_local.suppliersocr_entity = val.tiers_id
                	}
                },
                deep: true
            },
			'invoice_supplier_local.tier': {
                handler(val) {
                	if(val) {
	                	this.invoice_supplier_local.suppliersocr_tiers = val.tiers_id
	                	this.loadIbanCheck()
                	}
                },
                deep: true
            },
			// Le cours dépend de la devise et de la date de la facture : changer l'une ou l'autre en
			// appelle un autre, et laisser l'ancien afficherait une contre-valeur que l'enregistrement
			// démentirait.
			'invoice_supplier_local.suppliersocr_currency': 'loadExchangeRate',
			'invoice_supplier_local.suppliersocr_date': 'loadExchangeRate',
			// Le rattachement apparaît quand l'opérateur passe le TTC en négatif : la liste des factures
			// à annuler se charge alors, sinon le sélecteur s'ouvrirait vide jusqu'à la première frappe.
			// Elle se recharge à chaque fois : une liste déjà remplie peut être celle d'un autre
			// fournisseur, choisi avant la correction.
			is_credit_note(is_credit) {
				if(is_credit) {
					this.searchCreditable('')
				}
			},
			// Les factures qu'un avoir peut annuler sont celles de son fournisseur et de sa comptabilité :
			// corriger l'un ou l'autre en change la liste, et rend caduques celles déjà désignées, que le
			// serveur refuserait. L'ancienne valeur absente est l'initialisation du formulaire, pas une
			// correction.
			'invoice_supplier_local.suppliersocr_tiers': 'onCreditScopeChanged',
			'invoice_supplier_local.suppliersocr_entity': 'onCreditScopeChanged',
			// Le formatage d'un montant demande les décimales de sa devise, qui se chargent : il ne peut
			// pas se faire dans le rendu, et se dépose ici à chaque fois que les montants bougent.
			converted_amounts: {
				async handler(amounts) {
					if(!amounts) {
						this.converted_display = null
						return
					}

					this.converted_display = {
						ht: await Common.priceFormat(amounts.ht, this.ledger_currency, true),
						ttc: await Common.priceFormat(amounts.ttc, this.ledger_currency, true)
					}
				},
				immediate: true
			}
		},

		computed: {
			/**
			 * Devise dans laquelle la comptabilité est tenue, et vers laquelle les montants du document
			 * sont convertis.
			 */
			ledger_currency() {
				return 'EUR'
			},

			/**
			 * Symbole de la devise du document, à apposer aux montants saisis.
			 *
			 * Il vient du navigateur plutôt que d'une table de correspondance : le référentiel des devises
			 * porte bien un symbole, mais l'utilitaire qui le lit rend l'euro quoi qu'on lui demande. Une
			 * devise dont le navigateur ne connaît pas le symbole rend son code, qui se lit tout aussi
			 * bien.
			 */
			currency_symbol() {
				// Comme le calcul voisin : la facture de travail n'existe qu'une fois le formulaire
				// initialisé, alors que les calculs sont évalués dès la création du composant.
				const code = (this.invoice_supplier_local && this.invoice_supplier_local.suppliersocr_currency) || this.ledger_currency

				try {
					return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: code })
						.formatToParts(0)
						.find(part => part.type === 'currency')
						.value
				}
				catch(e) {
					return code
				}
			},

			/**
			 * Facture libellée dans une autre devise que celle de la comptabilité : ses montants
			 * s'affichent tels que le document les porte, et leur contrepartie comptable se calcule.
			 */
			is_foreign_currency() {
				// La facture de travail n'existe qu'une fois le formulaire initialisé, alors que ce
				// calcul est évalué dès la création du composant pour établir ses dépendances.
				if(!this.invoice_supplier_local) {
					return false
				}

				const currency = this.invoice_supplier_local.suppliersocr_currency
				return !!currency && currency !== this.ledger_currency
			},

			/**
			 * Contrepartie comptable des montants saisis, recalculée à la frappe plutôt que reprise de
			 * la facture : c'est ce que l'enregistrement va écrire, et l'utilisateur doit le voir avant
			 * de valider, pas après.
			 */
			converted_amounts() {
				if(!this.is_foreign_currency || !this.exchange_rate) {
					return null
				}

				const rate = this.exchange_rate.rate
				const amount = value => String(value).toFloat() || 0

				return {
					ht: amount(this.invoice_supplier_local.suppliersocr_ht) / rate,
					ttc: amount(this.invoice_supplier_local.suppliersocr_ttc) / rate
				}
			},

			/**
			 * Facture reçue par voie électronique : les données qu'elle déclare — numéro, date,
			 * montants, devise — viennent du document et ne se corrigent pas au clavier. Elles
			 * identifient la facture auprès de la plateforme dans nos comptes rendus, et servent de
			 * référence au fournisseur comme à l'administration.
			 *
			 * Le test porte sur la valeur explicite : une facture dont la provenance n'est pas connue
			 * reste modifiable, plutôt que verrouillée à tort.
			 */
			from_xml() {
				return this.invoice_supplier.suppliersocr_manual === false
			},

			/**
			 * La pièce est un avoir. Rien ne le déclare en base : un avoir est une facture à montant
			 * négatif, et c'est son signe qui le dit. Le TTC est lu sur le formulaire et non sur la
			 * facture reçue, pour que le rattachement s'ouvre dès que l'opérateur corrige le signe, sans
			 * qu'il ait à enregistrer puis revenir.
			 */
			is_credit_note() {
				return this.invoice_supplier_local !== null && String(this.invoice_supplier_local.suppliersocr_ttc).toFloat() < 0
			},

			/**
			 * Numéros des factures nouvellement désignées que l'avoir ne couvre plus : il s'est épuisé sur
			 * celles qui les précèdent, et la compensation ne leur imputerait rien. Même parcours que le
			 * serveur, qui les refuse — les factures déjà rattachées d'abord, puis les nouvelles dans
			 * l'ordre de leur désignation, celles qui ne doivent plus rien étant passées.
			 *
			 * L'avoir se mesure dans la devise de tenue, comme le reste dû des factures : sur son TTC tant
			 * qu'aucun règlement ne l'a touché, sur son reste dû ensuite.
			 */
			uncovered_credit_invoices() {
				if(!this.is_credit_note) {
					return []
				}

				const amount = String(this.can_edit ? this.invoice_supplier_local.suppliersocr_ttc : this.invoice_supplier_local.suppliersocr_balance).toFloat() || 0
				const rate = this.is_foreign_currency && this.exchange_rate ? this.exchange_rate.rate : 1
				const attached = this.invoice_supplier.credit_invoices.map(invoice => invoice.suppliersocr_id)

				let remaining = Math.round(Math.abs(Math.min(0, amount)) / rate * 100)
				const uncovered = []

				this.credit_invoices.forEach(invoice => {
					if(invoice.suppliersocr_balance <= 0) {
						return
					}

					if(remaining === 0 && !attached.includes(invoice.suppliersocr_id)) {
						uncovered.push(invoice.suppliersocr_num)
						return
					}

					remaining -= Math.min(remaining, invoice.suppliersocr_balance)
				})

				return uncovered
			},

			/**
			 * Le compte de règlement annoncé par la facture attend une décision.
			 *
			 * Une facture sans IBAN et une facture dont l'IBAN est déjà approuvé passent également :
			 * dans le premier cas il n'y a rien à comparer, dans le second la comparaison est faite.
			 * Tout le reste — premier compte du fournisseur, compte différent de ceux connus, compte
			 * laissé en quarantaine — se règle avant le contrôle, pas après.
			 */
			iban_to_arbitrate() {
				if(!this.iban_check || this.iban_arbitrated) {
					return false
				}

				return !['no_iban', 'known_approved'].includes(this.iban_check.status)
			},

			/**
			 * La prise en charge peut avoir été annoncée hors de Groomy — depuis l'interface de la
			 * plateforme, ou avant une reprise de données : le cycle de vie fait alors foi, et il n'y a
			 * plus rien à déclarer même si notre propre contrôle n'a pas encore eu lieu.
			 */
			is_checked() {
				return this.invoice_supplier.suppliersocr_ocr_status === 'checked' || !!this.invoice_supplier.suppliersocr_acknowledged
			},

			/**
			 * Bon à payer déjà acquis : par notre propre approbation, ou par un statut annoncé au
			 * fournisseur — approbation posée sur la plateforme, règlement transmis, encaissement
			 * déclaré. Dans tous ces cas, il n'y a plus rien à approuver.
			 */
			is_approved() {
				return !!this.invoice_supplier.suppliersocr_approved
			},

			// Facture rejetée ou close par la plateforme : le cycle n'accepte plus rien, et le serveur
			// refuserait ce que l'écran proposerait encore.
			is_closed() {
				return !!this.invoice_supplier.suppliersocr_lifecycle_terminal
			}
		},

		components: {
            CurrencyInput : () => import('GroomyRoot/components/Inputs/CurrencyInput'),
            WarnAlert : () => import('GroomyRoot/components/Alert/WarnAlert'),
            SupplierSuggestion : () => import('@/components/InvoiceSupplier/SupplierSuggestion'),
            IbanControl : () => import('@/components/InvoiceSupplier/IbanControl')
		}
	}

</script>